import axios from "../../services/axios.config";

const endpoints = {
  addProject: `api/projects/add-new-project/`,
  getAllProjects: `api/projects/`,
  getProjectById: `api/projects/`,
  removeProject: `api/projects/delete-project/`,
  editProject: `api/projects/edit-project/`,
  togglePublishFlag: `api/projects/toggle-publish/`,
  getProjectNameListOnly: `api/projects/list/`,
  getProjectPlatform: `api/platforms/`,
  getProjectCategory: `api/categories/`,
  getProjectStatus: `api/status/`,
  getLanguage: `api/language/`,
};

const state = {
  projects: [],
  projectDetail: {},
  projectNameList: [],
  projectPlatform: [],
  projectStatus: [],
  projectCategory: [],
  languages: [],
};

const getters = {
  getProjects: (state) => state.projects,
  getProjectNameList: (state) => state.projectNameList,
  getProjectDetails: (state) => state.projectDetail,
  getProjectPlatformList: (state) => state.projectPlatform,
  getProjectCategoryList: (state) => state.projectCategory,
  getProjectStatusList: (state) => state.projectStatus,
  getLanguages: (state) => state.languages,
};

const actions = {
  fetchProjectList: ({commit}, filterQuery) => {
    return new Promise((resolve, reject) => {
      axios
        .get(endpoints.getAllProjects + filterQuery)
        .then((response) => {
          commit("setProjectList", response.data);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  fetchProjectNameOnly: ({commit}) => {
    return new Promise((resolve, reject) => {
      axios
        .get(endpoints.getProjectNameListOnly)
        .then((response) => {
          commit("setProjectNameList", response.data);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  fetchProjectPlatform: ({commit}) => {
    return new Promise((resolve, reject) => {
      axios
        .get(endpoints.getProjectPlatform)
        .then((response) => {
          commit("setProjectPlatformList", response.data);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },
  fetchProjectStatus: ({commit}) => {
    return new Promise((resolve, reject) => {
      axios
        .get(endpoints.getProjectStatus)
        .then((response) => {
          commit("setProjectStatusList", response.data);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },
  fetchProjectCategory: ({commit}) => {
    return new Promise((resolve, reject) => {
      axios
        .get(endpoints.getProjectCategory)
        .then((response) => {
          commit("setProjectCategoryList", response.data);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },
  fetchLanguage: ({commit}) => {
    return new Promise((resolve, reject) => {
      axios
        .get(endpoints.getLanguage)
        .then((response) => {
          commit("setLanguageList", response.data);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  addProjects: ({commit}, project) => {
    return new Promise((resolve, reject) => {
      axios
        .post(endpoints.addProject, project)
        .then((response) => {
          commit("addNewProject", response.data);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },
  editProject: ({commit}, value) => {
    return new Promise((resolve, reject) => {
      const formData = value.projectForm;
      const oldProjectName = String(
        value.oldProjectName || formData.previous_project_name || ""
      ).trim();
      axios
        .put(endpoints.editProject + `${value.id}`, formData)
        .then(async (response) => {
          commit("updateProjectListData", formData);
          const newName = String(formData.project_name || "").trim();
          if (
            oldProjectName &&
            newName &&
            oldProjectName !== newName &&
            value.syncKonekoBudgetRename !== false
          ) {
            try {
              const {syncKonekoProjectRename} = await import(
                "@/services/konekoProjectRenameApi"
              );
              await syncKonekoProjectRename(value.id, oldProjectName, newName);
            } catch {
              /* budget sync is best-effort; project save already succeeded */
            }
          }
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  deleteProject: ({commit}, project) => {
    return new Promise((resolve, reject) => {
      axios
        .delete(endpoints.removeProject + `${project.id}`)
        .then((response) => {
          commit("deleteProjectData", project.id);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  fetchProjectDetails({commit}, value) {
    return new Promise((resolve, reject) => {
      axios
        .get(endpoints.getProjectById + value.id)
        .then((response) => {
          commit("setProjectDetail", response.data);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },
  toggleProjectPublishFlag({commit}, {projectId, newPublishState}) {
    return new Promise(() => {
      axios({
        method: "PUT",
        url: endpoints.togglePublishFlag + projectId,
        data: {
          publish_website_flag: newPublishState,
        },
      }).then(() => {
        commit("updateToggle", {projectId, newPublishState});
      });
    });
  },
};

const mutations = {
  setProjectList: (state, projectsData) => {
    state.projects = projectsData.sort((a, b) =>
      a.project_priority > b.project_priority ? 1 : -1
    );
  },

  setProjectNameList: (state, data) => (state.projectNameList = data),
  setProjectPlatformList: (state, data) => (state.projectPlatform = data),
  setProjectStatusList: (state, data) => (state.projectStatus = data),
  setProjectCategoryList: (state, data) => (state.projectCategory = data),
  setLanguageList: (state, data) => (state.languages = data),
  addNewProject: (state, payload) => {
    state.projects.push(payload);
  },

  updateProjectListData: (state, updatedProject) => {
    state.projects = state.projects.map((oneProject) => {
      return oneProject.id === updatedProject.id ? updatedProject : oneProject;
    });
  },

  updateToggle: (state, {projectId, newPublishState}) => {
    state.projects = state.projects.map((oneProject) => {
      if (oneProject.id === projectId) {
        oneProject.publish_website_flag = newPublishState;
      }
      return oneProject;
    });
  },

  deleteProjectData: (state, id) => {
    const i = state.projects.findIndex((item) => {
      if (item.id !== id) return;
      return id;
    });
    if (i === -1) return;
    state.projects.splice(i, 1);
  },

  setProjectDetail: (state, projectDetailData) =>
    (state.projectDetail = projectDetailData),
};

export default {
  state,
  getters,
  actions,
  mutations,
};
